import React from 'react';
import { createRoot } from 'react-dom/client';

import { ModalConfirm } from './ModalConfirm';

import { IModalConfirmProps, IModalFuncProps, TConfigUpdate } from '../types';

export const destroyFns: Array<() => void> = [];

export default function confirm(config: IModalFuncProps) {
  const container = document.createDocumentFragment();
  const root = createRoot(container);

  let currentConfig: IModalFuncProps = { ...config, close, visible: true, open: true };

  function destroy<T extends { triggerCancel?: boolean }>(...args: T[]) {
    if (root) {
      setTimeout(() => {
        root.unmount();
      });
    }

    const triggerCancel = args.some(param => param?.triggerCancel);

    if (config.onCancel && triggerCancel) {
      config.onCancel(...args);
    }

    for (let i = 0; i < destroyFns.length; i++) {
      const fn = destroyFns[i];

      if (fn === close) {
        destroyFns.splice(i, 1);
        break;
      }
    }
  }

  function render(props: IModalConfirmProps) {
    /**
     * https://github.com/ant-design/ant-design/issues/23623
     *
     * Sync render blocks React event. Let's make this async.
     */
    setTimeout(() => {
      root.render(<ModalConfirm {...props} />);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function close(...args: any[]) {
    currentConfig = {
      ...currentConfig,
      visible: false,
      open: false,
      afterClose: () => {
        if (typeof config.afterClose === 'function') {
          config.afterClose();
        }
        destroy(...args);
      },
    };
    render(currentConfig);
  }

  function update(configUpdate: TConfigUpdate) {
    if (typeof configUpdate === 'function') {
      currentConfig = configUpdate(currentConfig);
    } else {
      currentConfig = {
        ...currentConfig,
        ...configUpdate,
      };
    }
    render(currentConfig);
  }

  render(currentConfig);

  destroyFns.push(close);

  return {
    destroy: close,
    update,
  };
}
